import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BatchSelector from '../../../Components/BatchSelector';

const EvaluationList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get('page')) || 1,
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    Number(searchParams.get('pageSize')) || 25,
  );

  const defaultBatchId = searchParams.get('batch') || '';
  const [batchId, setBatchId] = useState(defaultBatchId);

  const [loading, setLoading] = useState(true);
  const [evaluates, setEvaluates] = useState({ users: [], total: 0 });

  // Fetch Evaluation Data
  const fetchEvaluates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getMentorEvaluations({
        batchId,
        page: currentPage,
        pageSize: itemsPerPage,
      });
      setEvaluates(data);
    } catch (error) {
      console.error('Failed to fetch evaluation data:', error);
    } finally {
      setLoading(false);
    }
  }, [batchId, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchEvaluates();
    // Update URL search params
    setSearchParams({
      batch: batchId,
      page: currentPage,
      pageSize: itemsPerPage,
    });
  }, [fetchEvaluates, batchId, currentPage, itemsPerPage, setSearchParams]);

  const handlePageSize = (size) => {
    setItemsPerPage(Number(size));
    setCurrentPage(1);
  };

  const handleBatchId = (id) => {
    setBatchId(id);
    setCurrentPage(1);
  };

  const handleNavigateToPage = (pageNumber) => {
    if (pageNumber < 1) return;
    setCurrentPage(pageNumber);
  };

  return (
    <div className='card card-body border-0 shadow-sm rounded-4'>
      {/* Header Section */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-3'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: '40px', height: '40px' }}
          >
            <i className='bi bi-people-fill text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>Evaluasi Mahasiswa</h1>
            <p
              className='text-muted mb-0 d-none d-lg-block'
              style={{ fontSize: '0.75rem' }}
            >
              Kelola dan pantau progres evaluasi bimbingan Anda.
            </p>
          </div>
        </div>

        <div className='d-flex flex-column align-items-md-end gap-2 ms-auto'>
          {/* Add Button */}
          <button
            onClick={() => navigate('/mentor/mentees/students')}
            className='btn btn-primary btn-sm rounded-pill px-3 fw-bold d-flex align-items-center gap-2 shadow-sm mb-1'
            style={{ fontSize: '0.8rem' }}
            title='Hubungkan mahasiswa baru ke akun bimbingan Anda'
          >
            <i className='bi bi-person-plus-fill'></i>
            <span>Tambah Mahasiswa</span>
          </button>

          <div className='d-flex flex-wrap align-items-center gap-3 justify-content-md-end'>
            {/* Page Size Selector */}
            <div className='d-flex align-items-center'>
              <label className='me-2 small text-muted'>Tampilkan:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => handlePageSize(e.target.value)}
                className='form-select form-select-sm shadow-sm rounded-pill px-3'
                style={{ minWidth: '80px', fontSize: '0.8rem' }}
              >
                {[25, 50, 100].map((size) => (
                  <option
                    key={size}
                    value={size}
                  >
                    {size}
                  </option>
                ))}
              </select>
            </div>

            {/* Batch Filter Selector */}
            <div className='d-flex align-items-center ms-1'>
              <label className='me-2 fw-medium text-muted small mb-0 d-none d-xl-inline'>
                Batch:
              </label>
              <div style={{ minWidth: '150px' }}>
                <BatchSelector
                  value={batchId}
                  onChange={handleBatchId}
                  placeholder='Pilih Batch'
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Alert for Student Assignment */}
      <div
        className='alert alert-primary bg-primary bg-opacity-10 border-0 rounded-3 d-flex align-items-center gap-3 p-3 mb-4'
        style={{ fontSize: '0.75rem' }}
      >
        <i className='bi bi-info-circle-fill text-primary fs-5'></i>
        <div className='text-dark'>
          Belum melihat mahasiswa bimbingan Anda? Gunakan tombol{' '}
          <strong>Tambah Mahasiswa</strong> di pojok kanan atas untuk memilih
          dan menetapkan (*assign*) mahasiswa baru secara langsung ke dalam
          daftar bimbingan Anda.
        </div>
      </div>

      <div className='table-responsive'>
        <table className='table table-hover align-middle mb-0'>
          <thead className='bg-light text-muted small text-uppercase fw-bold'>
            <tr>
              <th
                className='ps-4 py-3 border-0'
                style={{ width: '50px' }}
              >
                #
              </th>
              <th className='py-3 border-0'>Mentor</th>
              <th className='py-3 border-0'>Mahasiswa</th>
              <th className='py-3 border-0'>Jurusan & Universitas</th>
              <th className='py-3 border-0'>Divisi</th>
              <th className='py-3 border-0 text-center'>Nilai</th>
              <th
                className='pe-4 py-3 border-0 text-center'
                style={{ width: '180px' }}
              >
                Progres PAT
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr
                  key={i}
                  className='placeholder-glow'
                >
                  <td className='ps-4 py-3'>
                    <span className='placeholder col-6 rounded'></span>
                  </td>
                  <td className='py-3'>
                    <span className='placeholder col-8 rounded'></span>
                  </td>
                  <td className='py-3'>
                    <span className='placeholder col-10 rounded'></span>
                  </td>
                  <td className='py-3'>
                    <span className='placeholder col-12 rounded'></span>
                  </td>
                  <td className='py-3'>
                    <span className='placeholder col-8 rounded'></span>
                  </td>
                  <td className='py-3 text-center'>
                    <span className='placeholder col-4 rounded'></span>
                  </td>
                  <td className='pe-4 py-3'>
                    <span className='placeholder col-12 rounded py-3'></span>
                  </td>
                </tr>
              ))
            ) : evaluates?.users?.length > 0 ? (
              evaluates.users.map((evaluate, key) => (
                <tr
                  key={key}
                  onClick={() =>
                    navigate(
                      `/mentor/mentees/${evaluate.CLPUsersId}/evaluations`,
                    )
                  }
                  className='transition-all cursor-pointer'
                >
                  <td className='ps-4 text-muted small py-3'>
                    {(currentPage - 1) * itemsPerPage + (key + 1)}
                  </td>
                  <td className='py-3'>
                    <div className='fw-semibold text-dark extra-small'>
                      {evaluate.mentors.firstName || '-'}{' '}
                      {evaluate.mentors.lastName || ''}
                    </div>
                  </td>
                  <td className='py-3'>
                    <div className='fw-bold text-primary mb-0 extra-small'>
                      {evaluate.firstName} {evaluate.lastName}
                    </div>
                    <span
                      className='text-muted'
                      style={{ fontSize: '0.65rem' }}
                    >
                      Mahasiswa Inbound
                    </span>
                  </td>
                  <td className='py-3'>
                    <div className='extra-small fw-medium text-dark'>
                      {evaluate.universities.universitiesMajor || '-'}
                    </div>
                    <div
                      className='text-muted'
                      style={{ fontSize: '0.65rem' }}
                    >
                      {evaluate.universities.universitiesName || '-'}
                    </div>
                  </td>
                  <td className='py-3 text-center'>
                    <span className='badge bg-light text-dark border extra-small fw-normal rounded-pill px-2 py-1'>
                      {evaluate?.institutions?.divisionName || '-'}
                    </span>
                  </td>
                  <td className='py-3 text-center'>
                    <div className='fw-bold text-dark'>
                      {evaluate?.evaluationFinal?.finalEvaluationAverage || '-'}
                    </div>
                  </td>
                  <td className='pe-4 py-3'>
                    <div className='d-flex flex-column gap-1'>
                      <div
                        className='d-flex justify-content-between text-muted mb-1'
                        style={{ fontSize: '0.65rem' }}
                      >
                        <span>
                          Done: {evaluate.progressPAT.hasEvaluated}/
                          {evaluate.progressPAT.totalUsersDivision}
                        </span>
                        <span className='fw-bold'>
                          {evaluate.progressPAT.progress}%
                        </span>
                      </div>
                      <div
                        className='progress shadow-none rounded-pill'
                        style={{ height: '6px', backgroundColor: '#f0f0f0' }}
                      >
                        <div
                          className={`progress-bar rounded-pill transition-all ${
                            evaluate.progressPAT.progress === 100
                              ? 'bg-success'
                              : 'bg-primary'
                          }`}
                          style={{ width: evaluate.progressPAT.progress + '%' }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan='7'
                  className='text-center py-5'
                >
                  <div className='text-muted mb-3 opacity-25'>
                    <i className='bi bi-inbox fs-1'></i>
                  </div>
                  <h6 className='fw-bold text-dark mb-1'>
                    Tidak ada data ditemukan
                  </h6>
                  <p className='text-muted small mb-0'>
                    Gunakan filter batch untuk menemukan data lainnya.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && evaluates?.users?.length > 0 && (
        <div className='mt-4 pt-4 border-top'>
          <nav aria-label='Evaluation navigation'>
            <ul className='pagination pagination-sm justify-content-center mb-0 gap-2'>
              <li
                className={`page-item ${!evaluates.previous ? 'disabled' : ''}`}
              >
                <button
                  onClick={() => handleNavigateToPage(evaluates.previous)}
                  className='page-link rounded-pill border-0 bg-light text-dark px-3 fw-bold'
                >
                  <i className='bi bi-chevron-left'></i>
                </button>
              </li>
              <li className='page-item active'>
                <span className='page-link rounded-pill border-0 bg-primary text-white px-3 fw-bold shadow-sm'>
                  {evaluates.current + 1 || currentPage}
                </span>
              </li>
              <li className={`page-item ${!evaluates.next ? 'disabled' : ''}`}>
                <button
                  onClick={() => handleNavigateToPage(evaluates.next + 1)}
                  className='page-link rounded-pill border-0 bg-light text-dark px-3 fw-bold'
                >
                  <i className='bi bi-chevron-right'></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      <style>{`
        .extra-small { font-size: 0.7rem; }
        .transition-all { transition: all 0.2s ease; }
        .cursor-pointer { cursor: pointer; }
        .table-hover tbody tr:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.03) !important;
          transform: scale(0.998);
        }
      `}</style>
    </div>
  );
};

export default EvaluationList;
